import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { useBoolean, useRecentGetter } from '@lib/common';
import { useCallback, useEffect, useMemo, useState, useReducer } from 'react';
import { useSubject } from '@lib/common/hooks/use-subject';
import { CompanyMasters } from '@api/masters';
import fuzzysort from 'fuzzysort';
import { useSubscription } from '@lib/common/hooks/use-subscription';
import { debounceTime, switchMap } from 'rxjs/operators';
import { asyncScheduler } from 'rxjs';
import { ServiceDialogContent } from './ServiceDialogContent_v4';
import { rbac } from '@lib/common';

export interface ReducerState {
  okPressed: boolean;
}
export const InitialState: ReducerState = { okPressed: false };

export function reducer(state, action) {
  switch (action.type) {
    case 'okPressed':
      return { okPressed: true };
    case 'reset':
      return { okPressed: false };
  }
}

export const ServicesPicker = ({
  services,
  onServiceAdd = null,
  defaultSelected,
  isLock=true,
  edit=false,
}: {
  services: CompanyMasters.Service[];
  onServiceAdd?: (services: CompanyMasters.Service[]) => any;
  defaultSelected: CompanyMasters.Service[];
  isLock:boolean;
  edit:boolean;
}) => {
  const [serviceOptions, setServiceOptions] =
    useState<Array<CompanyMasters.Service & { highlighted?: string }>>(services);

  useEffect(() => {
    setServiceOptions(services);
  }, [services]);

  const [dialogOpen, openDialog, closeDialog] = useBoolean(false);
 /*  const searchTargets = useMemo(
    () => services.map(s => ({ ...s, prepared: fuzzysort.prepare(s.service +"("+ s.service_aka+")") })),
    [services]
    ); */
  //Ticket-13165 - empty() is attached to service name on search
  const searchTargets = useMemo(
    () =>
      services.map(s => {
        const aka = s.service_aka?.trim();
        const label = aka ? `${s.service} (${aka})` : s.service;

        return {
          ...s,
          prepared: fuzzysort.prepare(label),
        };
      }),
    [services]
  );
  const resetResults = () => {
    setServiceOptions(services);
  };

  const state = useRecentGetter({ searchTargets, setServiceOptions, serviceOptions, resetResults });

  const search$ = useSubject<string>();

  const [reducerState, dispatch] = useReducer(reducer, InitialState);

  useSubscription(() =>
    search$
      .pipe(
        debounceTime(300, asyncScheduler),
        switchMap(txt =>
          !txt.trim()
            ? Promise.resolve(null)
            : fuzzysort.goAsync(txt, state().searchTargets, {
                key: 'prepared',
                limit: 50,
                threshold: -1000,
              })
        )
      )
      .subscribe(results => {
        if (!results) {
          return state().resetResults();
        }

        state().setServiceOptions(
          results.map(r => ({
            ...r.obj,
            highlighted: fuzzysort.highlight(r, '<strong>', '</strong>'),
          }))
        );
      })
  );

  const onSearch = useCallback(
    evt => {
      search$.next(evt.target.value);
    },
    [search$]
  );

  const handleServiceClick = useCallback(
    (srvs: CompanyMasters.Service[]) => {
      closeDialog();
      setTimeout(() => {
        onServiceAdd && onServiceAdd(srvs.map(srv => ({ id: srv.id, service: srv.service,hierarchy_level:srv.hierarchy_level,ultimate_parent:srv.ultimate_parent })));
        dispatch({ type: 'reset' });
      }, 100);
    },
    [closeDialog, onServiceAdd]
  );

  return (
    <>
      <Button
        onClick={openDialog}
        disabled={isLock && edit}
        color="primary"
        variant="contained">
        Add / Edit
      </Button>
      <Dialog
        // Disable transitions to speed up the element interaction
        // and fix multiple addtion on consecutive clicks.
        transitionDuration={0}
        fullWidth
        maxWidth="xs"
        open={dialogOpen}
        onClose={closeDialog}>
        <DialogTitle>Browse Services</DialogTitle>
        <DialogContent style={{ height: '60vh' }} dividers>
          <ServiceDialogContent
            serviceOptions={serviceOptions}
            servicesLength={services.length}
            onSearch={onSearch}
            handleServiceClick={handleServiceClick}
            reducerState={reducerState}
            defaultSelected={defaultSelected}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch({ type: 'okPressed' })}>Ok</Button>
          <Button onClick={closeDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
